import { z } from 'zod';
import { ColorSchema, LOOK, NameSchema, about, numberFormat, sourceOf, transform } from '../schema_fields.ts';

/**
 * A table cell (legacy `TextComponent`): fixed text, or a number from one of the tile's sources
 * with its format, transform and colour.
 */
export const TableCellSchema = z.union([
    z.string().meta(about('Text')),
    z.object({
        source: sourceOf(NameSchema, { gives: ['number'], label: 'Reading' }),
        numberFormat: numberFormat('0'),
        transform: transform(),
        color: ColorSchema.optional().meta(about('Colour', undefined, 'color')),
    }).meta(about('Reading')),
]);

/** Where a table has lines between its rows or its columns (table-tile.md TT-3). */
export const TABLE_LINES = ['none', 'first', 'all'] as const;

/**
 * A table (legacy `TableTile`): a header row of column labels, then rows of cells. Columns are
 * as wide as their `width` relative to the others; rows share the height. Every row has a cell
 * for each column. All text has one size (table-tile.md TT-1); the labels may be bold and the
 * rows and columns divided by lines (TT-2, TT-3).
 */
export const TableOptionsSchema = z
    .object({
        columns: z
            .array(z.object({
                label: z.string().meta(about('Label', 'at the top of the column')),
                width: z.number().positive().default(1).meta(about('Width', 'relative to the other columns')),
            }).meta({ title: 'Column' }))
            .min(1)
            .meta(about('Columns')),
        rows: z.array(z.array(TableCellSchema)).meta(about('Rows', 'a cell for each column', 'table-rows')),
        boldFirstRow: z.boolean().default(false).meta({ ...LOOK, ...about('Bold first row', 'the column labels in bold') }),
        rowLines: z.enum(TABLE_LINES).default('none').meta({
            ...LOOK,
            ...about('Row lines', 'lines between the rows'),
            'x-labels': { none: 'None', first: 'Below the first row', all: 'Between all rows' },
        }),
        columnLines: z.enum(TABLE_LINES).default('none').meta({
            ...LOOK,
            ...about('Column lines', 'lines between the columns'),
            'x-labels': { none: 'None', first: 'After the first column', all: 'Between all columns' },
        }),
    })
    .superRefine((table, context) => {
        for (const [index, row] of table.rows.entries()) {
            if (row.length !== table.columns.length) {
                context.addIssue({ code: 'custom', path: ['rows', index], message: `has ${row.length} cells for ${table.columns.length} columns` });
            }
        }
    });
